import { useCallback, useEffect, useMemo, useState } from 'react';
import type { GiantId } from '../../../../domain/ids/ids';
import {
  areCardsMatching,
  CLAUDI_PAIR_PREVIEW_MS,
  CLAUDI_PAIR_TIME_LIMIT_SEC,
  CLAUDI_VICTORY_HOLD_MS,
  createClaudiPairDeck,
  pickClaudiPairGiantIds,
  type ClaudiPairCard,
} from '../../../../domain';
import {
  CLAUDI_CLOSED_CARD_URL,
  CLAUDI_OPEN_CARD_URL,
  CLAUDI_TILE_URL,
  CLAUDI_TIMER_URL,
  CLAUDI_VICTORY_URL,
  getClaudiPairImageUrl,
} from '../assets';
import './ClaudiPairsGame.css';

export type ClaudiPairsGameProps = {
  giantId: GiantId;
  onWin: () => void;
  onLose: () => void;
  onTimerChange?: (secondsLeft: number) => void;
};

type Phase = 'preview' | 'play' | 'victory';

/** Card slots from .dev/claudi/min-game.svg (2×3 grid). */
const CARD_SLOTS: ReadonlyArray<{ left: number; top: number }> = [
  { left: 193, top: 632 },
  { left: 565, top: 632 },
  { left: 193, top: 928 },
  { left: 565, top: 928 },
  { left: 193, top: 1223 },
  { left: 565, top: 1223 },
];

function formatClaudiTimer(secondsLeft: number): string {
  const safe = Math.max(0, secondsLeft);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} сек`;
}

export function ClaudiPairsGame({
  giantId,
  onWin,
  onLose,
  onTimerChange,
}: ClaudiPairsGameProps) {
  const deck = useMemo(
    () => createClaudiPairDeck(pickClaudiPairGiantIds(giantId)),
    [giantId],
  );
  const [phase, setPhase] = useState<Phase>('preview');
  const [openIds, setOpenIds] = useState<string[]>(() => deck.map((c) => c.id));
  const [matchedIds, setMatchedIds] = useState<string[]>([]);
  const [secondsLeft, setSecondsLeft] = useState<number>(CLAUDI_PAIR_TIME_LIMIT_SEC);
  const [busy, setBusy] = useState(false);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    onTimerChange?.(secondsLeft);
  }, [onTimerChange, secondsLeft]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setOpenIds([]);
      setPhase('play');
    }, CLAUDI_PAIR_PREVIEW_MS);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (phase !== 'play' || finished) {
      return;
    }
    const timer = window.setInterval(() => {
      setSecondsLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [finished, phase]);

  useEffect(() => {
    if (finished || phase !== 'play' || secondsLeft > 0) {
      return;
    }
    setFinished(true);
    onLose();
  }, [finished, onLose, phase, secondsLeft]);

  useEffect(() => {
    if (phase !== 'victory') {
      return;
    }
    const timer = window.setTimeout(() => {
      onWin();
    }, CLAUDI_VICTORY_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [onWin, phase]);

  const cardById = useMemo(() => {
    const map = new Map<string, ClaudiPairCard>();
    for (const card of deck) {
      map.set(card.id, card);
    }
    return map;
  }, [deck]);

  const handleCardClick = useCallback(
    (cardId: string) => {
      if (finished || busy || phase !== 'play') {
        return;
      }
      if (matchedIds.includes(cardId) || openIds.includes(cardId)) {
        return;
      }
      if (openIds.length >= 2) {
        return;
      }

      const nextOpen = [...openIds, cardId];
      setOpenIds(nextOpen);
      if (nextOpen.length < 2) {
        return;
      }

      const [a, b] = nextOpen;
      const cardA = cardById.get(a);
      const cardB = cardById.get(b);
      if (cardA == null || cardB == null) {
        return;
      }

      if (areCardsMatching(cardA.pairId, cardB.pairId)) {
        const nextMatched = [...matchedIds, a, b];
        setMatchedIds(nextMatched);
        setOpenIds([]);
        if (nextMatched.length >= deck.length) {
          setFinished(true);
          setPhase('victory');
        }
        return;
      }

      setBusy(true);
      window.setTimeout(() => {
        setOpenIds([]);
        setBusy(false);
      }, 450);
    },
    [busy, cardById, deck.length, finished, matchedIds, openIds, phase],
  );

  return (
    <div className="claudi-pairs">
      <img className="claudi-pairs__tile" src={CLAUDI_TILE_URL} alt="" draggable={false} />
      <h2 className="claudi-pairs__title">Запомни пары</h2>
      {phase === 'preview' || phase === 'play' ? (
        <p className="claudi-pairs__hint">Найди их по памяти</p>
      ) : null}

      {deck.map((card, index) => {
        const slot = CARD_SLOTS[index] ?? CARD_SLOTS[0]!;
        const isMatched = matchedIds.includes(card.id);
        const isOpen = phase === 'preview' || openIds.includes(card.id) || isMatched;
        return (
          <button
            key={card.id}
            type="button"
            className={`claudi-pairs__card${isOpen ? ' is-open' : ' is-closed'}${
              isMatched ? ' is-matched' : ''
            }`}
            style={{ left: slot.left, top: slot.top }}
            onClick={() => handleCardClick(card.id)}
            disabled={finished || busy || phase !== 'play' || isMatched}
          >
            {/* <img> instead of CSS background-image — VK WebView drops url() in CSS vars. */}
            <img
              className="claudi-pairs__card-chrome"
              src={isOpen ? CLAUDI_OPEN_CARD_URL : CLAUDI_CLOSED_CARD_URL}
              alt=""
              draggable={false}
            />
            {isOpen ? (
              <img
                className="claudi-pairs__card-face"
                src={getClaudiPairImageUrl(card.giantId)}
                alt=""
                draggable={false}
              />
            ) : null}
          </button>
        );
      })}

      <div className="claudi-pairs__timer">
        <img className="claudi-pairs__timer-art" src={CLAUDI_TIMER_URL} alt="" draggable={false} />
        <p className="claudi-pairs__timer-value">{formatClaudiTimer(secondsLeft)}</p>
      </div>

      {phase === 'victory' ? (
        <div className="claudi-pairs__victory" aria-live="polite">
          <img
            className="claudi-pairs__victory-art"
            src={CLAUDI_VICTORY_URL}
            alt="Отличная память!"
            draggable={false}
          />
        </div>
      ) : null}
    </div>
  );
}
